import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUserStore } from "../../store/store";
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
} from "../../Components/Common/Tailwind";
import QuantityButtonInput from "../../Components/Common/QuantityButtonInput";
import { DetailImgModal, DetailInfoModal, DetailUseAdviceModal } from "../../Components/modal/ManagerModal";
import LoginPromptModal from "../../Components/modal/LoginPromptModal";
import { ticketService } from "../../api/services/ticket.service";
import MobileViewContainer from "../../Components/MobileViewContainer";
import AmCarousel from "../../Components/AmCarousel";
import { ChevronLeft } from "lucide-react";
import { useTicketCartStore } from "../../store/ticketCartStore";

const TicketItemPage = () => {
    const navigate = useNavigate();

    const { id } = useParams();
    const [permission] = useUserStore((state) => [state.permission]);

    const [product, setProduct] = useState({});

    const [detailImageEditModal, setDetailImageEditModal] = useState(false);
    const [detailInfoModal, setDetailInfoModal] = useState(false);
    const [detailUseAdviceModal, setDetailUseAdviceModal] = useState(false);

    const [modal, setModal] = useState(false);
    const [user] = useUserStore((state) => [state.user]);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const setCartItemQuantity = useTicketCartStore((state) => state.setItemQuantity);
    const cartItems = useTicketCartStore((state) => state.items);

    const toggleModal = () => setModal(!modal);

    const quantity = useMemo(() => {
        const cartItem = cartItems.find((item) => String(item.id) === String(id));
        return cartItem?.quantity || 0;
    }, [cartItems, id]);

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                const productResponse = await ticketService.getTicketProductById(id);
                const productData = productResponse.data;
                const normalizedProduct = user && user.membership && productData.membership_price && productData.membership_price < productData.price
                    ? {
                        ...productData,
                        price: productData.membership_price,
                        membershipApplied: true,
                    }
                    : {
                        ...productData,
                        membershipApplied: false,
                    };

                const syncResult = useTicketCartStore.getState().syncProduct(normalizedProduct);

                if (syncResult.adjusted) {
                    toast.info("재고 변동으로 장바구니 수량이 조정되었습니다.");
                }

                setProduct(normalizedProduct);
            } catch (error) {
                console.log(error);
            }
        };

        fetchProductData();
    }, [id, user]);

    const handlePurchase = () => {
        if (!user) {
            setShowLoginModal(true);
            return;
        }

        if (!product.id || !quantity) {
            return;
        }

        navigate(`/order-ticket-multi`, {
            state: { type: 1 },
        });
    };

    return (
        <MobileViewContainer>
            {/* Header */}
          
            <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-160 z-40 bg-white/80 backdrop-blur-md px-4 py-6 border-b border-gray-200/60 shadow-sm">
                <div className="relative flex items-center justify-center">
                    <Link to="/ticket" className="absolute left-0 flex items-center text-black no-underline">
                        <ChevronLeft size={24} />
                    </Link>
                    <p className="text-xl font-medium" style={{ fontFamily: "Gmarket-B" }}>티켓 상세</p>
                </div>
            </div>

            <div className="bg-gray-100 min-h-screen" style={{ paddingTop: "84px", paddingBottom: "120px" }}>
                {/* Image Card */}
                <div className="mx-4 mt-4">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ position: "relative" }}>
                        {!!permission && permission.permission > 0 ? (
                            <Button variant="ghost" className="border-0 btn-soft-warning" style={{ position: "absolute", top: "12px", right: "12px", zIndex: "10" }} onClick={() => setDetailImageEditModal(!detailImageEditModal)}>
                                상세이미지 수정
                            </Button>
                        ) : null}
                        <div className="product-img-slider">
                            {product && product.images?.length ?
                                <AmCarousel
                                    interval={4000}
                                    heightRatio={0.65}
                                    objectFit="cover"
                                    items={product.images?.map((item, index) => {
                                        if (typeof item === "string") return { src: item, key: index };
                                        return item;
                                    })}
                                />
                                : <div className="bg-gray-200 w-full h-56" />
                            }
                        </div>
                    </div>
                </div>

                {/* Title & Price Card */}
                <div className="mx-4 mt-3 bg-white rounded-2xl shadow-sm px-5 py-4">
                    <h2 style={{ fontFamily: "Gmarket-B", fontSize: "20px", color: "#111", margin: "0 0 8px 0" }}>
                        {product.name}
                    </h2>
                    {product.membershipApplied && (
                        <div style={{ fontSize: "11px", color: "#22c55e", marginBottom: "6px" }}>멤버십 가격 적용됨</div>
                    )}
                    <p style={{ margin: 0 }}>
                        <span style={{ fontFamily: "Gmarket-B", fontSize: "22px", color: "#111", fontWeight: "bold" }}>
                            {product.price?.toLocaleString()}
                        </span>
                        <span style={{ fontFamily: "Gmarket-B", fontSize: "18px", color: "#111", fontWeight: "bold", marginLeft: "2px" }}>
                            원
                        </span>
                    </p>
                </div>

                {/* 상세정보 + 이용안내 카드 */}
                <div className="mx-4 mt-3 mb-3 bg-white rounded-2xl shadow-sm overflow-hidden">
                    {/* 상세정보 섹션 */}
                    <div style={{ padding: "36px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div style={{ width: "4px", height: "22px", backgroundColor: "#e16036", borderRadius: "2px", flexShrink: 0 }} />
                                <span style={{ fontFamily: "Gmarket-B", fontSize: "16px", fontWeight:"bold" , color: "#6b7280" }}>상세정보</span>
                            </div>
                            {!!permission && permission.permission > 0 ? (
                                <Button variant="ghost" className="border-0 btn-soft-info" style={{ fontSize: "12px" }} onClick={() => setDetailInfoModal(!detailInfoModal)}>
                                    상세정보 수정
                                </Button>
                            ) : null}
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: product.descript }} style={{ fontSize: "16px", lineHeight: "1.8", color: "#6b7280" }} />
                    </div>

                    {/* 섹션 구분선 */}
                    <div style={{ height: "1px", backgroundColor: "#EEEEEE", margin: "0 20px" }} />

                    {/* 이용안내 섹션 */}
                    <div style={{ padding: "36px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div style={{ width: "4px", height: "22px", backgroundColor: "#e16036", borderRadius: "2px", flexShrink: 0 }} />
                                <span style={{ fontFamily: "Gmarket-B", fontSize: "16px", color: "#6b7280", fontWeight: "bold" }}>이용안내</span>
                            </div>
                            {!!permission && permission.permission > 0 ? (
                                <Button variant="ghost" className="border-0 btn-soft-info" style={{ fontSize: "12px" }} onClick={() => setDetailUseAdviceModal(!detailUseAdviceModal)}>
                                    이용안내 수정
                                </Button>
                            ) : null}
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: product["using_info"] }} style={{ fontSize: "15px", lineHeight: "1.8", color: "#6b7280" }} />
                    </div>
                </div>
            </div>

            {/* Fixed Bottom Action Bar — matches CardSection's bottom bar style */}
            <div
                className="fixed left-1/2 -translate-x-1/2 w-full bg-white/70 backdrop-blur-md border-t border-gray-200 z-50"
                style={{ bottom: 0, maxWidth: "640px", boxShadow: "0 -4px 16px rgba(0,0,0,0.10)" }}
            >
                <div className="flex items-center gap-3 px-6 py-4">
                    {/* Left: 담기 (목록 페이지와 동일한 버튼 컴포넌트) */}
                    <QuantityButtonInput
                        size={32}
                        quantity={quantity}
                        onChange={(value) => {
                            if (!product.id) {
                                return;
                            }
                            if (product?.remaining_stock && value > product.remaining_stock) {
                                toast.error("구매 가능 개수를 초과하였습니다.");
                                return;
                            }
                            setCartItemQuantity(product, value);
                        }}
                    />

                    {/* Right: 바로 구매하기 */}
                    <Button
                        variant="primary"
                        disabled={quantity === 0}
                        className="flex-1 rounded-xl font-bold"
                        style={{ fontFamily: "Gmarket-M", fontSize: "15px", height: "44px" }}
                        onClick={handlePurchase}
                    >
                        티켓 결제하기
                    </Button>
                </div>
            </div>

            <Modal isOpen={modal} toggle={toggleModal}>
                <ModalBody>
                    <span style={{ fontFamily: "Gmarket-M" }}>필수 옵션을 선택해 주세요.</span>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={toggleModal} color="primary">
                        확인
                    </Button>
                </ModalFooter>
            </Modal>

            <DetailImgModal setVisible={setDetailImageEditModal} visible={detailImageEditModal} itemId={product.id} type={"ticket"} navigate={navigate} />
            <DetailInfoModal setVisible={setDetailInfoModal} visible={detailInfoModal} itemId={product.id} type={"ticket"} navigate={navigate} />
            <DetailUseAdviceModal setVisible={setDetailUseAdviceModal} visible={detailUseAdviceModal} itemId={product.id} type={"ticket"} navigate={navigate} />
            <LoginPromptModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </MobileViewContainer>
    );
};

export default TicketItemPage;
